import { computed, getCurrentInstance, onUnmounted, toValue, type MaybeRefOrGetter } from "vue";
import { delay } from "@shared-utils/date.utils";
import useLoadingState from "@shared-composables/useLoadingState";

const loadedKeys = new Set<string>();

/**
 * Combien d'instances vivantes ont posé chaque clé au setup. Le store de chargement dédoublonne
 * sans compter : deux listes qui partagent une clé n'y laissent qu'une entrée, et la lever au
 * démontage de la première éteindrait le skeleton de la seconde.
 */
const pendingByKey = new Map<string, number>();

export const MIN_SKELETON_DURATION = 1000;

export default function useSilentRefresh(key: MaybeRefOrGetter<string>)
{
	const { loadingStart, loadingEnd, loadingStore } = useLoadingState();
	const isFirstLoad = computed(() => !loadedKeys.has(toValue(key)));
	const isLoading = computed(() => loadingStore.isKeyLoading(toValue(key)));

	// Skeleton immédiat au premier load (avant que refresh() soit appelé)
	if (isFirstLoad.value)
	{
		// La clé du setup est mémorisée : une clé réactive peut changer en cours de vie, et c'est
		// bien celle-ci qu'il faudra lever au démontage, pas celle du moment.
		const postedKey = toValue(key);

		loadingStart(postedKey);
		pendingByKey.set(postedKey, (pendingByKey.get(postedKey) ?? 0) + 1);

		// Sans ça, un composant monté puis démonté avant que `refresh()` ait eu lieu — ou avant
		// qu'il se termine — laissait sa clé dans la pile pour le reste de la session, et tout
		// écran suivant `isLoading` global restait figé.
		if (getCurrentInstance())
		{
			onUnmounted(() =>
			{
				const remaining = (pendingByKey.get(postedKey) ?? 1) - 1;

				if (remaining > 0)
				{
					pendingByKey.set(postedKey, remaining);
					return;
				}

				pendingByKey.delete(postedKey);
				// Sans effet si `refresh()` l'a déjà levée : `disableLoading` ignore une clé absente.
				loadingEnd(postedKey);
			});
		}
	}

	const refresh = async (fetchData: () => Promise<void>) =>
	{
		// Résolu à chaque appel : la clé peut être réactive (ex. navigation entre séances sans
		// remount du composant → l'id change en cours de vie).
		const k = toValue(key);

		if (!loadedKeys.has(k) || loadingStore.isKeyLoading(k))
		{
			if (!loadingStore.isKeyLoading(k))
			{
				loadingStart(k);
			}

			try
			{
				await Promise.all([
					fetchData(),
					delay(MIN_SKELETON_DURATION)
				]);
				loadedKeys.add(k);
			}
			finally
			{
				// Skeleton toujours levé, même si fetchData throw/timeout (sinon skeleton figé).
				loadingEnd(k);
			}
		}
		else
		{
			await fetchData();
		}
	};

	return { isFirstLoad, isLoading, refresh };
}

export function resetAllSilentRefresh()
{
	loadedKeys.clear();
	pendingByKey.clear();
}
